/**
 * Compression message component - shows when chat history is compressed
 * Based on gemini-cli CompressionMessage with web UI styling
 */

import { AlertCircle, Package } from 'lucide-react';
import { ChatCompressionInfo } from '@/../../shared-types/src';

interface CompressionMessageProps {
  compressionInfo: ChatCompressionInfo;
  isPending?: boolean;
}

export function CompressionMessage({ compressionInfo, isPending = false }: CompressionMessageProps) {

  return (
    <div className="flex items-center justify-center py-4">
      <div className="flex items-center gap-3 px-4 py-3 bg-accent border border-primary/30 rounded-lg max-w-2xl">
        <div className="flex-shrink-0">
          {isPending ? (
            <div className="animate-spin">
              <Package className="h-5 w-5 text-primary dark:text-accent-foreground" />
            </div>
          ) : (
            <AlertCircle className="h-5 w-5 text-primary dark:text-accent-foreground" />
          )}
        </div>
        
        <div className="flex-1">
          <p className="text-sm font-medium text-accent-foreground mb-1">
            {isPending ? 'Compressing Context' : 'Context Compressed'}
          </p>
          <p className="text-xs text-accent-foreground">
            {isPending ? (
              'Conversation approaching token limit. Creating summary...'
            ) : (
              <>
                <span className="font-medium">IMPORTANT:</span> This conversation approached the input token limit. 
                A compressed context will be used for future messages.
              </>
            )}
          </p>
          
          {!isPending && compressionInfo.originalTokenCount && compressionInfo.newTokenCount && (
            <div className="mt-2 flex items-center gap-4 text-xs text-primary dark:text-accent-foreground">
              <span>
                <span className="font-medium">Original:</span> {compressionInfo.originalTokenCount.toLocaleString()}
              </span>
              <span>→</span>
              <span>
                <span className="font-medium">Compressed:</span> {compressionInfo.newTokenCount.toLocaleString()}
              </span>
              <span className="text-primary dark:text-accent-foreground">
                ({Math.round((1 - compressionInfo.newTokenCount / compressionInfo.originalTokenCount) * 100)}% reduction)
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}